import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const memoriesRef = (memorialId) =>
  collection(db, "memorials", memorialId, "memories");

const submissionsRef = (memorialId) =>
  collection(db, "memorials", memorialId, "submissions");

/**
 * Subscribe to live memory documents of a given type for a memorial.
 * Returns an unsubscribe function.
 */
export function subscribeToMemories(memorialId, type, sortOrder, onUpdate) {
  const q = query(
    memoriesRef(memorialId),
    where("type", "==", type),
    orderBy("createdAt", sortOrder)
  );
  return onSnapshot(q, (snap) =>
    onUpdate(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
}

/**
 * Subscribe to all owner-uploaded images for pin selection.
 * Only call this when the current user is an owner.
 */
export function subscribeToOwnerImages(memorialId, onUpdate) {
  const q = query(
    memoriesRef(memorialId),
    where("type", "==", "image"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) =>
    onUpdate(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
}

/**
 * Subscribe to all notes for pin selection.
 * Only call this when the current user is an owner.
 */
export function subscribeToOwnerNotes(memorialId, onUpdate) {
  const q = query(
    memoriesRef(memorialId),
    where("type", "==", "note"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) =>
    onUpdate(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
}

/**
 * Subscribe to pending submissions for owner moderation.
 * Sorts client-side to avoid a composite Firestore index requirement.
 */
export function subscribeToPendingSubmissions(memorialId, onUpdate) {
  const q = query(submissionsRef(memorialId), where("status", "==", "pending"));
  return onSnapshot(q, (snap) => {
    const items = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort(
        (a, b) =>
          (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0)
      );
    onUpdate(items);
  });
}

/**
 * Add a new memory document (note or image) to a memorial.
 */
export function addMemory(memorialId, payload) {
  return addDoc(memoriesRef(memorialId), {
    ...payload,
    createdAt: serverTimestamp(),
  });
}

/**
 * Update the list of pinned image IDs on a memorial document.
 */
export function setPinnedImages(memorialId, imageIds) {
  return updateDoc(doc(db, "memorials", memorialId), {
    pinnedImageIds: imageIds,
  });
}

/**
 * Update the pinned note ID on a memorial document.
 */
export function setPinnedNote(memorialId, noteId) {
  return updateDoc(doc(db, "memorials", memorialId), {
    pinnedNoteId: noteId,
  });
}
